import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../lib/api";
import AdminSection from "../components/admin/AdminSection";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Crown, Check, Sparkle, CaretRight, ShieldCheck, Lightning, CreditCard, ArrowLeft, UploadSimple, X, Lock, Shield, ArrowRight } from "@phosphor-icons/react";

// ── Trust Modal (reutilizable) ─────────────────────────────────────────────────
function TrustModal({ isTrial = true, amount = "$50", onConfirm, onCancel, onNoCard, loading }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(8px)" }}>
      <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden"
        style={{ background: "#0D0D12", border: "1px solid rgba(212,175,55,0.2)" }}>
        <div style={{ height: 3, background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />
        <div className="px-6 pt-6 pb-8">
          <div className="flex items-start justify-between mb-6">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.2)" }}>
              <Shield weight="fill" className="w-6 h-6" style={{ color: "#D4AF37" }} />
            </div>
            <button onClick={onCancel} className="text-zinc-600 hover:text-zinc-400 transition mt-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          {isTrial ? (
            <>
              <h2 className="text-2xl font-black text-white mb-1">No te cobramos hoy.</h2>
              <p className="text-zinc-400 text-sm mb-7">Tu tarjeta solo se guarda como garantía — igual que cuando rentas un carro.</p>
              <div className="space-y-0 mb-7">
                {[
                  { n: "1", color: "emerald", title: "Ahora — Guardas tu tarjeta", sub: "$0 cobrados. Acceso inmediato al 100% de la plataforma." },
                  { n: "2", color: "amber",   title: "24 horas — Reloj en marcha",  sub: "Verás un contador en tu dashboard. Cancela con un solo click." },
                  { n: "3", color: "red",     title: "Solo si decides quedarte — Se cobra", sub: `Si no cancelas en 24h, se activa tu suscripción de ${amount}.` },
                ].map(({ n, color, title, sub }, i, arr) => (
                  <div key={n} className="flex gap-4 items-start">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-black text-sm"
                        style={{ background: `rgba(${color==="emerald"?"16,185,129":color==="amber"?"212,175,55":"239,68,68"},0.15)`, border: `1px solid rgba(${color==="emerald"?"16,185,129":color==="amber"?"212,175,55":"239,68,68"},0.3)`, color: color==="emerald"?"#10B981":color==="amber"?"#D4AF37":"#ef4444" }}>{n}</div>
                      {i < arr.length - 1 && <div className="w-0.5 h-8 mt-1" style={{ background: "rgba(255,255,255,0.06)" }} />}
                    </div>
                    <div className="pt-1 pb-5">
                      <p className="text-white font-bold text-sm">{title}</p>
                      <p className="text-zinc-500 text-xs mt-0.5">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-black text-white mb-1">Pago seguro con Stripe</h2>
              <p className="text-zinc-400 text-sm mb-7">Se cobrarán <strong className="text-white">{amount}</strong> a tu tarjeta al completar el pago.</p>
              <div className="flex items-start gap-3 p-4 rounded-xl mb-7"
                style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.15)" }}>
                <ShieldCheck weight="fill" className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-white">Cancela cuando quieras</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Sin contratos. Cancelas en cualquier momento desde tu perfil y no se renueva.</p>
                </div>
              </div>
            </>
          )}

          <div className="flex items-center gap-3 mb-7 p-3 rounded-xl"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <Lock className="w-4 h-4 flex-shrink-0" style={{ color: "#D4AF37" }} />
            <div>
              <p className="text-xs font-bold text-white">100% seguro — procesado por Stripe</p>
              <p className="text-[11px] text-zinc-500">El mismo sistema que usan Netflix, Amazon y Spotify.</p>
            </div>
          </div>

          <button onClick={onConfirm} disabled={loading}
            className="w-full py-4 rounded-2xl font-black text-sm tracking-widest uppercase transition flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #D4AF37, #B8962E)", color: "#000" }}
            data-testid="trust-modal-confirm">
            {loading
              ? <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              : <><ArrowRight weight="bold" className="w-4 h-4" /> {isTrial ? "Entendido — continuar al pago seguro" : "Continuar al pago seguro"}</>
            }
          </button>
          {onNoCard && isTrial && (
            <button onClick={onNoCard} disabled={loading}
              data-testid="trust-modal-no-card"
              className="w-full mt-3 py-3.5 rounded-2xl text-sm font-bold transition border flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", color: "#d1d5db" }}>
              <Clock className="w-4 h-4" />
              No tengo tarjeta — Quiero 1 día gratis sin tarjeta
            </button>
          )}
          <button onClick={onCancel} disabled={loading}
            className="w-full mt-2 py-2.5 text-xs text-zinc-600 hover:text-zinc-400 transition font-semibold">
            Volver
          </button>
        </div>
      </div>
    </div>
  );
}

const getCards = (weekly = "50", monthly = "160") => [
  {
    id: "trial",
    price: "0",
    periodKey: "plans.per_2days",
    icon: Sparkle,
    highlight: false,
    fKeys: ["trial_f1", "trial_f2", "trial_f3", "trial_f4"],
  },
  {
    id: "weekly",
    price: weekly,
    periodKey: "plans.per_week",
    icon: Lightning,
    highlight: true,
    fKeys: ["weekly_f1", "weekly_f2", "weekly_f3", "weekly_f4", "weekly_f5"],
  },
  {
    id: "monthly",
    price: monthly,
    periodKey: "plans.per_month",
    icon: Crown,
    highlight: false,
    badgeKey: "plans.monthly_badge",
    fKeys: ["monthly_f1", "monthly_f2", "monthly_f3", "monthly_f4"],
  },
];

const METHOD_META = {
  stripe:     { labelKey: "plans.stripe_label", color: "text-blue-400",   border: "border-blue-500/30" },
  zelle:      { label: "Zelle",                  color: "text-purple-400", border: "border-purple-500/30" },
  pago_movil: { label: "Pago Móvil",             color: "text-sky-400",    border: "border-sky-500/30" },
  binance:    { label: "Binance (USDT)",          color: "text-yellow-400", border: "border-yellow-500/30" },
};

// Brand logos reales (subidas por el usuario)
// Logos locales (antes estaban en el CDN de Emergent — migrados para independencia)
const PAYMENT_LOGOS = {
  stripe:     "/img/payments/stripe.png",
  zelle:      "/img/payments/zelle.jpg",
  binance:    "/img/payments/binance.png",
};

function PaymentLogo({ method, size = 40 }) {
  if (method === "pago_movil") {
    return (
      <div style={{ width: size, height: size, borderRadius: 10, background: "linear-gradient(135deg,#0EA5E9,#0284C7)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink: 0 }}>
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="white">
          <rect x="7" y="2" width="10" height="20" rx="2" ry="2" stroke="white" strokeWidth="2" fill="none"/>
          <circle cx="12" cy="18" r="1" fill="white"/>
        </svg>
      </div>
    );
  }
  const logo = PAYMENT_LOGOS[method];
  if (!logo) return null;
  const isZelle = method === "zelle";
  const isStripe = method === "stripe";
  return (
    <div style={{
      width: isStripe ? 72 : size, height: size,
      borderRadius: isStripe ? 8 : 10,
      overflow: "hidden", flexShrink: 0,
      background: isZelle ? "transparent" : isStripe ? "transparent" : "#3A3A1A",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <img
        src={logo} alt={METHOD_META[method]?.label || method}
        loading="lazy"
        style={{ width: "100%", height: "100%", objectFit: isStripe ? "contain" : "cover", borderRadius: isStripe ? 8 : 10 }}
      />
    </div>
  );
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function PaymentModal({ plan, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [step, setStep] = useState("method"); // method | details
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showTrustModal, setShowTrustModal] = useState(false);
  const nav = useNavigate();

  const loadMethods = useCallback(async () => {
    try {
      const res = await apiClient.get("/payment-methods");
      setPaymentMethods(res.data.methods || []);
    } catch { /* silent */ }
    setLoadingMethods(false);
  }, []);

  useEffect(() => { loadMethods(); }, [loadMethods]);

  const handleSelectMethod = (type) => {
    setSelectedMethod(type);
    if (type === "stripe") {
      setShowTrustModal(true); // Show trust modal FIRST
    } else {
      setStep("details");
    }
  };

  const handleStripeCheckout = async () => {
    setSubmitting(true);
    try {
      const res = await apiClient.post("/payments/checkout", {
        plan_id: plan.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
      setShowTrustModal(false);
      setSubmitting(false);
    }
  };

  const handleNoCardTrial = async () => {
    setSubmitting(true);
    try {
      const res = await apiClient.post("/subscription/activate-no-card-trial");
      toast.success(res.data?.message || "¡24 horas de acceso activadas!");
      setShowTrustModal(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo activar el día gratuito.");
      setShowTrustModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("plans.proof_formats"));
      return;
    }
    const b64 = await toBase64(file);
    setProofFile(file);
    setProofPreview(b64);
  };

  const handleSubmitManual = async () => {
    if (!proofPreview) {
      toast.error(t("plans.proof_title"));
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/payments/manual", {
        plan_id: plan.id,
        payment_method: selectedMethod,
        payment_proof_image: proofPreview,
      });
      toast.success(t("plans.proof_sent"));
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
    setSubmitting(false);
  };

  const methodData = paymentMethods.find(m => m.type === selectedMethod);
  const meta = METHOD_META[selectedMethod] || {};
  const metaLabel = meta.labelKey ? t(meta.labelKey) : (meta.label || selectedMethod);

  return (
    <>
      {/* Trust Modal — intercepta el pago con Stripe */}
      {showTrustModal && (
        <TrustModal
          isTrial={plan.id === "trial"}
          amount={`$${plan.price}${plan.id === "weekly" ? "/semana" : "/mes"}`}
          onConfirm={handleStripeCheckout}
          onCancel={() => { setShowTrustModal(false); setSubmitting(false); }}
          onNoCard={plan.id === "trial" ? handleNoCardTrial : undefined}
          loading={submitting}
        />
      )}

    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="surface rounded-2xl border border-white/[0.1] w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            {step === "details" && (
              <button onClick={() => setStep("method")} className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm mb-1 transition-colors">
                <ArrowLeft className="w-4 h-4" /> {t("plans.back")}
              </button>
            )}
            <h2 className="font-black text-lg">
              {step === "method" ? t("plans.how_to_pay") : t("plans.pay_by", { method: metaLabel })}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t("plans.plan_label")} {t(`plans.${plan.id}_name`)} — <span className="text-yellow-400 font-bold">${plan.price}{t(plan.periodKey)}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06] text-zinc-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === "method" && (
          <div className="space-y-2.5">
            {/* Stripe always available */}
            <button
              onClick={() => handleSelectMethod("stripe")}
              disabled={submitting}
              data-testid="pm-select-stripe"
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10 transition-all text-left disabled:opacity-50"
            >
              <PaymentLogo method="stripe" size={40} />
              <div className="flex-1">
                <div className="font-bold text-sm text-blue-400">{t("plans.stripe_label")}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{t("plans.stripe_desc")}</div>
              </div>
              <CaretRight className="w-4 h-4 text-zinc-600" />
            </button>

            {loadingMethods ? (
              <div className="h-20 rounded-xl bg-white/[0.04] animate-pulse" />
            ) : paymentMethods.length === 0 ? (
              <p className="text-xs text-zinc-600 text-center py-4">{t("plans.card_only_stripe")}</p>
            ) : (
              paymentMethods.map(m => {
                const mMeta = METHOD_META[m.type] || {};
                return (
                  <button
                    key={m.id}
                    onClick={() => handleSelectMethod(m.type)}
                    data-testid={`pm-select-${m.type}`}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border ${mMeta.border || "border-white/[0.1]"} bg-white/[0.03] hover:bg-white/[0.06] transition-all text-left`}
                  >
                    <PaymentLogo method={m.type} size={40} />
                    <div className="flex-1">
                      <div className={`font-bold text-sm ${mMeta.color || "text-white"}`}>{m.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {m.account_info ? `${m.account_info}` : t("plans.manual_desc")}
                      </div>
                    </div>
                    <CaretRight className="w-4 h-4 text-zinc-600" />
                  </button>
                );
              })
            )}
          </div>
        )}

        {step === "details" && methodData && (
          <div className="space-y-4">
            {/* Account info card con logo del método */}
            <div className={`rounded-xl p-4 border ${meta.border || "border-white/[0.1]"} bg-white/[0.03] space-y-3`}>
              <div className="flex items-center gap-3 pb-2 border-b border-white/[0.06]">
                <PaymentLogo method={selectedMethod} size={36} />
                <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold">{t("plans.payment_data")}</p>
              </div>
              <div className="space-y-2 text-sm">
                {methodData.account_name && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t("plans.holder")}:</span>
                    <span className="font-bold text-white">{methodData.account_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-zinc-500">{t("plans.account")}:</span>
                  <span className={`font-bold font-mono ${meta.color}`}>{methodData.account_info}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">{t("plans.amount")}:</span>
                  <span className="font-black text-emerald-400 text-base">${plan.price}</span>
                </div>
              </div>
              {methodData.instructions && (
                <p className="text-xs text-zinc-400 mt-2 border-t border-white/[0.06] pt-3">{methodData.instructions}</p>
              )}
            </div>

            {/* Proof upload */}
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-2">{t("plans.proof_title")}</p>
              {proofPreview ? (
                <div className="relative">
                  <img src={proofPreview} alt={t("plans.proof_title")} className="w-full max-h-48 object-contain rounded-xl border border-white/[0.08] bg-black" />
                  <button
                    onClick={() => { setProofFile(null); setProofPreview(null); }}
                    className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="proof-upload"
                  data-testid="proof-upload-area"
                  className="flex flex-col items-center justify-center gap-2 w-full h-32 rounded-xl border-2 border-dashed border-white/[0.15] hover:border-yellow-500/40 cursor-pointer transition-all text-zinc-500 hover:text-zinc-300"
                >
                  <UploadSimple className="w-6 h-6" />
                  <span className="text-sm">{t("plans.proof_placeholder")}</span>
                  <span className="text-xs text-zinc-600">{t("plans.proof_formats")}</span>
                  <input
                    id="proof-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              )}
            </div>

            <button
              onClick={handleSubmitManual}
              disabled={submitting || !proofPreview}
              data-testid="submit-manual-payment"
              className="btn-primary w-full disabled:opacity-50"
            >
              {submitting ? t("plans.sending") : t("plans.send_proof")}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default function Plans() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [prices, setPrices] = useState({ weekly: "50", monthly: "160" });
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/public/pricing`).then(r => r.json()).then(d => {
      setPrices({ weekly: String(d.weekly || 50), monthly: String(d.monthly || 160) });
    }).catch(() => {});
  }, []);
  const CARDS = getCards(prices.weekly, prices.monthly);
  const [paymentModal, setPaymentModal] = useState(null); // { plan }
  const nav = useNavigate();

  const onSubscribe = async (planId) => {
    if (!user) {
      nav("/registro");
      return;
    }
    if (planId === "trial") {
      // If already on trial or active, show appropriate message
      if (user.subscription_status === "trialing") {
        toast.info("Ya tienes un trial activo. Tu acceso está vigente.");
        return;
      }
      if (user.subscription_status === "active") {
        toast.info("Ya tienes una suscripción activa.");
        return;
      }
      // Logged-in user without subscription → open payment modal for trial
      const plan = CARDS.find(c => c.id === planId);
      setPaymentModal({ plan });
      return;
    }
    const plan = CARDS.find(c => c.id === planId);
    setPaymentModal({ plan });
  };

  const trialActive = user?.subscription_status === "trialing";
  const premiumActive = user?.subscription_status === "active";

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 pt-8 pb-16">
      {paymentModal && (
        <PaymentModal
          plan={paymentModal.plan}
          onClose={() => setPaymentModal(null)}
          onSuccess={() => refresh()}
        />
      )}

      <div className="mb-8 text-center max-w-2xl mx-auto">
        <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-2">{t("plans.title")}</div>
        <h1 className="text-display font-black text-3xl lg:text-5xl tracking-tighter leading-tight">
          {t("plans.title")}
        </h1>
        <p className="text-zinc-400 mt-3 text-sm">
          {t("plans.sub")}
        </p>
      </div>

      {/* Current plan status */}
      {user && (
        <div className="text-center mb-8">
          {premiumActive ? (
            <div className="inline-flex items-center gap-2 chip bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
              <ShieldCheck weight="fill" className="w-3.5 h-3.5" /> {t("plans.active_sub")}
            </div>
          ) : trialActive ? (
            <div className="inline-flex items-center gap-2 chip-yellow">
              <Sparkle weight="fill" className="w-3.5 h-3.5" /> {t("plans.trial_active")}
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 chip-muted">
              <div className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
              {t("plans.free_plan")}
            </div>
          )}
        </div>
      )}

      <AdminSection icon={Crown} title="Elige tu plan" subtitle="Sin permanencia · cancela cuando quieras" color="amber">
      <div className="grid md:grid-cols-3 gap-5" style={{ paddingTop: 12 }}>
        {CARDS.map((card) => {
          const Icon = card.icon;
          const isCurrent = (card.id === "trial" && trialActive) ||
                            (card.id !== "trial" && premiumActive);
          return (
            <div
              key={card.id}
              className={`relative rounded-2xl p-7 transition-all duration-300 flex flex-col ${
                card.highlight
                  ? "bg-gradient-to-b from-yellow-500/5 to-transparent border border-yellow-500/30"
                  : "surface hover:border-white/[0.15]"
              }`}
              data-testid={`plan-card-${card.id}`}
            >
              {card.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 chip-yellow">
                  <Sparkle weight="fill" className="w-3 h-3" /> {t("plans.most_chosen")}
                </div>
              )}
              {card.badgeKey && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 chip bg-emerald-500/10 text-emerald-400 border-emerald-500/30 !text-[10px]">
                  {t(card.badgeKey)}
                </div>
              )}

              <div className="flex items-center gap-3 mb-2">
                <Icon weight="fill" className="w-5 h-5 text-yellow-400" />
                <div className="text-display font-black text-2xl tracking-tight">{t(`plans.${card.id}_name`)}</div>
              </div>
              <div className="text-sm text-zinc-400 mb-6 leading-relaxed">{t(`plans.${card.id}_tagline`)}</div>

              <div className="flex items-end gap-1 mb-6">
                <span className="text-display font-black text-5xl tracking-tighter">${card.price}</span>
                <span className="text-zinc-500 text-sm mb-1.5">{t(card.periodKey)}</span>
              </div>

              <ul className="space-y-2.5 mb-7 flex-1">
                {card.fKeys.map((fk) => (
                  <li key={fk} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check weight="bold" className="w-4 h-4 mt-0.5 text-yellow-400 flex-shrink-0" />
                    <span>{t(`plans.${fk}`)}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`plan-subscribe-${card.id}`}
                onClick={() => onSubscribe(card.id)}
                disabled={loadingPlan === card.id || isCurrent}
                className={`w-full ${card.highlight ? "btn-primary" : "btn-secondary"} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loadingPlan === card.id ? (
                  t("plans.processing")
                ) : isCurrent ? (
                  <>{t("plans.plan_active")}</>
                ) : (
                  <>
                    {t(`plans.cta_${card.id}`)} <CaretRight weight="bold" className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
      </AdminSection>

      {/* Bottom trust signals */}
      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-zinc-500 tracking-wider">
        <span className="flex items-center gap-1.5"><Check weight="bold" className="w-3 h-3 text-emerald-400" /> {t("plans.trust_instant")}</span>
        <span className="flex items-center gap-1.5"><Check weight="bold" className="w-3 h-3 text-emerald-400" /> {t("plans.trust_cancel")}</span>
        <span className="flex items-center gap-1.5"><CreditCard weight="bold" className="w-3 h-3 text-emerald-400" /> {t("plans.multiple_methods")}</span>
      </div>
    </div>
  );
}

